'use client';

import { useQuery } from '@apollo/client';
import React from 'react';
import { GET_SHOP_CART_PAYMENT_STATUS } from '../graphql/queries';
// eslint-disable-next-line import/no-cycle
import { useClientPoints, useShopCart } from '../hooks';

export type TPaymentProcessContext = {
  remainingAmount: number;
  setRemainingAmount: React.Dispatch<React.SetStateAction<number>>;
  benefitsAmount: number;
  setBenefitsAmount: React.Dispatch<React.SetStateAction<number>>;
  scrollTargetRef?: React.RefObject<HTMLDivElement>;
  handleScroll?: () => void;
  refetchGtShopCartPaymentStatus?: () => void;
};

export const PaymentProcessContext =
  React.createContext<TPaymentProcessContext>({
    remainingAmount: 0,
    setRemainingAmount: () => {},
    benefitsAmount: 0,
    setBenefitsAmount: () => {},
    scrollTargetRef: null,
    handleScroll: () => {},
    refetchGtShopCartPaymentStatus: () => {},
  });

type Props = {
  children: React.ReactNode;
};

export function PaymentProcessProvider({ children }: Props) {
  const [shopCart] = useShopCart();
  const { benefits } = useClientPoints();
  const [remainingAmount, setRemainingAmount] = React.useState<number>(0);
  const [benefitsAmount, setBenefitsAmount] = React.useState<number>(0);
  const scrollTargetRef = React.useRef(null);

  const {
    data: dataGetShopCartPaymentStatus,
    refetch: refetchGtShopCartPaymentStatus,
  } = useQuery<{
    getShopCartPaymentStatus: {
      paymentStatus: string;
      remainingAmount: number;
    };
  }>(GET_SHOP_CART_PAYMENT_STATUS, {
    variables: {
      data: {
        shopCartId: shopCart?._id,
        benefitsApplied:
          benefitsAmount !== 0 ? benefits.slice(-benefitsAmount) : null,
      },
    },
  });

  const handleScroll = () => {
    if (scrollTargetRef.current) {
      const targetPosition =
        scrollTargetRef.current.getBoundingClientRect().top + window.scrollY;
      const offset = 100; // Ajusta este valor para controlar cuánto más arriba quieres el scroll
      scrollTargetRef.current.scrollIntoView({
        top: targetPosition - offset,
        behavior: 'smooth',
      });
    }
  };

  React.useEffect(() => {
    if (dataGetShopCartPaymentStatus) {
      setRemainingAmount(
        dataGetShopCartPaymentStatus?.getShopCartPaymentStatus?.remainingAmount
      );
    }
  }, [dataGetShopCartPaymentStatus]);

  React.useEffect(() => {
    refetchGtShopCartPaymentStatus();
  }, []);

  const value = React.useMemo(
    () => ({
      remainingAmount,
      setRemainingAmount,
      benefitsAmount,
      setBenefitsAmount,
      scrollTargetRef,
      handleScroll,
      refetchGtShopCartPaymentStatus,
    }),
    [remainingAmount]
  );

  return (
    <PaymentProcessContext.Provider value={value}>
      {children}
    </PaymentProcessContext.Provider>
  );
}
